/**
 * Writing a date input into the shape the EBTS field expects.
 *
 * An `<input type="date">` always hands back `YYYY-MM-DD`. Nearly every EBTS date field wants `CCYYMMDD`
 * — eight digits, no separators — so something has to convert. That conversion used to exist for exactly
 * one field, `dateOfBirth`, hardcoded in the builder. Any other date a workflow mapped went out verbatim:
 * `datePrinted` reaches 2.038 (DPR) on both card-conversion workflows and was being written as
 * "2026-08-24", which is the wrong length and the wrong character type for the field.
 *
 * Making the format part of the field definition fixes that generally and lets a workflow target a field
 * whose format is not the usual one, without another special case in the builder.
 *
 * Everything here is string surgery. Do NOT route a date through `new Date()` to reformat it: the string
 * is read as UTC and formatted in local time, which moves the day backwards across most of the US. That
 * bug shipped once already, turning 14 January into the 13th.
 */

/** The default for a date field, and what almost every EBTS date field wants. */
export const DEFAULT_DATE_FORMAT = 'yyyyMMdd';

/** Formats offered in the workflow editor. */
export const DATE_FORMATS: { value: string; label: string }[] = [
  { value: 'yyyyMMdd', label: 'yyyyMMdd — 20260824 (EBTS standard)' },
  { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd — 2026-08-24' },
  { value: 'MM/dd/yyyy', label: 'MM/dd/yyyy — 08/24/2026' },
  { value: 'dd/MM/yyyy', label: 'dd/MM/yyyy — 24/08/2026' },
  { value: 'MMddyyyy', label: 'MMddyyyy — 08242026' },
  { value: 'yyyyMM', label: 'yyyyMM — 202608' },
  { value: 'yyyy', label: 'yyyy — 2026' },
];

/** Year, month and day as written, or null when the value is not a date we can read. */
function partsOf(value: string): { yyyy: string; MM: string; dd: string } | null {
  const v = value.trim();
  if (!v) return null;

  // What a date input gives us.
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (iso) return { yyyy: iso[1]!, MM: iso[2]!, dd: iso[3]! };

  // Already EBTS-shaped — a stored draft, an imported EFT, or a value typed straight in.
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(v);
  if (compact) return { yyyy: compact[1]!, MM: compact[2]!, dd: compact[3]! };

  return null;
}

/**
 * Apply `format` to a date value.
 *
 * Anything unrecognised is returned UNCHANGED rather than guessed at. A half-typed date, a field holding
 * something that is not a date at all, or a format this does not know are all better left visible: the
 * FBI rejects a wrong value as readily as a missing one, and the rejection names the field, not the bug.
 */
export function formatDateValue(value: string, format: string = DEFAULT_DATE_FORMAT): string {
  const p = partsOf(value);
  if (!p) return value;
  // Longest tokens first, so `yyyy` is consumed before `yy` and `MM` before `M`.
  return format
    .replace(/yyyy/g, p.yyyy)
    .replace(/yy/g, p.yyyy.slice(2))
    .replace(/MM/g, p.MM)
    .replace(/dd/g, p.dd);
}
